import { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import DrawnApostrophe from './DrawnApostrophe'
import LangSwitcher from './LangSwitcher'
import { L, useT } from '../i18n/locale.jsx'
import { BOOKING_URL } from '../config'
import { calAttrs } from '../cal'

// Nav fix. Dispare la scroll în jos, reapare la scroll în sus. La scroll capătă un scrim
// subtil (blur + fundal translucid) ca wordmark-ul să rămână lizibil peste orice secțiune.
// Wordmark (→ home) + Solutions/Articole (outline) la stânga, CTA booking plin (honey) la dreapta.
export default function TopBar({ theme = 'dark' }) {
  const [hidden, setHidden] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const nav = useT().nav

  useEffect(() => {
    let last = window.scrollY
    let raf = 0
    const update = () => {
      const y = window.scrollY
      if (y > last + 4 && y > 120) setHidden(true)
      else if (y < last - 4) setHidden(false)
      setScrolled(y > 40)
      last = y
    }
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; update() })
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    update()
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [])

  const dark = theme === 'dark'
  const txt = dark ? 'text-cream' : 'text-ink'
  const muted = dark ? 'text-cream-muted' : 'text-ink-muted'
  const cta = `inline-flex min-h-[44px] items-center rounded-full border px-5 font-mono text-[11px] uppercase tracking-[0.1em] transition-colors duration-[950ms] ease-quint hover:border-honey hover:text-honey ${
    dark ? 'border-cream/25 text-cream' : 'border-ink/20 text-ink'
  }`
  // CTA primar — plin honey, iese în evidență (lead gen). Nu agresiv, dar vizibil.
  const ctaBook =
    'inline-flex min-h-[44px] items-center rounded-full bg-honey px-5 font-mono text-[11px] font-medium uppercase tracking-[0.1em] text-forest shadow-sm transition-colors duration-300 ease-quint hover:bg-honey-soft'
  const scrim = scrolled
    ? dark
      ? 'bg-forest/85 backdrop-blur-md border-b border-forest-line/40'
      : 'bg-bone/85 backdrop-blur-md border-b border-bone-line'
    : 'border-b border-transparent'

  return (
    <motion.header
      initial={{ y: -120 }}
      animate={{ y: hidden ? -120 : 0 }}
      transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${scrim}`}
    >
      <div className="mx-auto flex max-w-site items-center justify-between px-5 py-5 md:px-10">
        {/* stânga — Solutions + Articole (secundare, outline) */}
        <nav className="flex items-center gap-2 md:gap-3">
          <L to="/solutions" className={cta}>{nav.solutions}</L>
          <L to="/articole" className={`${cta} hidden sm:inline-flex`}>{nav.articles}</L>
        </nav>

        {/* mijloc — wordmark */}
        <L to="/" className={`flex min-h-[44px] items-baseline py-2 transition-colors duration-[950ms] ${txt}`}>
          <DrawnApostrophe />
          <span className="display-italic text-2xl leading-none md:text-[28px]">vorba</span>
          <span className={`ml-1 font-mono text-[11px] transition-colors duration-[950ms] ${muted}`}>.dev</span>
        </L>

        {/* dreapta — switcher limbă + CTA booking (primar) */}
        <div className="flex items-center gap-2 md:gap-4">
          <LangSwitcher theme={theme} />
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" {...calAttrs} className={`${ctaBook} hidden sm:inline-flex`}>
            {nav.book}
          </a>
        </div>
      </div>
    </motion.header>
  )
}
